'use client';

/**
 * CaseStudySelector — bespoke interactive case-study switcher.
 *
 * Source: content/pages/case-study-viewer/page.html
 *   - <select id="casestudy"> with one <option> per case study
 *   - jQuery driver that on change updates URL ?casestudy=, swaps the visible
 *     <iframe> (one per study), and updates the heading entity span.
 *
 * Rebuilt as a client component using React state + useSearchParams. Each case
 * study has a stable id (matches Magento's hyphenated lowercase rule), an
 * `<option>` value (original mixed-case label), and a PDF URL.
 */

import { useEffect, useState } from 'react';
import { useSearchParams } from 'next/navigation';

interface CaseStudy {
  id: string;
  label: string;
  pdfUrl: string;
  /** When true the option is excluded from the visible <select> dropdown but
   * still renders its iframe + Download link for parity. */
  hiddenInSelect?: boolean;
}

interface CaseStudySelectorProps {
  caseStudies: CaseStudy[];
  defaultId: string;
}

function normalizeId(label: string): string {
  return label.toLowerCase().replace(/\s/g, '-');
}

export function CaseStudySelector({ caseStudies, defaultId }: CaseStudySelectorProps) {
  const searchParams = useSearchParams();
  const [selectedId, setSelectedId] = useState<string>(() => {
    const fromUrl = searchParams.get('casestudy');
    if (fromUrl) {
      const id = normalizeId(fromUrl);
      if (caseStudies.some((c) => c.id === id)) return id;
    }
    return defaultId;
  });

  useEffect(() => {
    const fromUrl = searchParams.get('casestudy');
    if (!fromUrl) return;
    const id = normalizeId(fromUrl);
    if (caseStudies.some((c) => c.id === id) && id !== selectedId) {
      setSelectedId(id);
    }
  }, [searchParams, caseStudies, selectedId]);

  const onChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const label = e.target.value;
    const id = normalizeId(label);
    setSelectedId(id);
    if (typeof window !== 'undefined') {
      window.history.pushState(
        {},
        '',
        `${window.location.pathname}?casestudy=${encodeURIComponent(label)}`,
      );
    }
  };

  const selected = caseStudies.find((c) => c.id === selectedId) ?? caseStudies[0];
  const selectedLabel = selected?.label ?? '';

  return (
    <div className="case-study-selector">
      <h2 className="case-study-heading">
        SCW and <span id="entity">{selectedLabel}</span> Case Study
      </h2>
      <form className="case-study-form" onSubmit={(e) => e.preventDefault()}>
        <label htmlFor="casestudy" className="case-study-label">
          Choose a case study:
        </label>
        <select
          id="casestudy"
          name="casestudy"
          value={selectedLabel}
          onChange={onChange}
          className="case-study-select"
        >
          {caseStudies
            .filter((c) => !c.hiddenInSelect)
            .map((c) => (
              <option key={c.id} value={c.label}>
                {c.label}
              </option>
            ))}
        </select>
      </form>

      {caseStudies.map((c) => (
        <div
          key={c.id}
          id={c.id}
          className="case-study-container"
          style={{ display: c.id === selectedId ? 'block' : 'none' }}
        >
          <iframe
            src={c.pdfUrl}
            title={`${c.label} Case Study`}
            className="case-study-iframe"
            loading="lazy"
          />
          <a href={c.pdfUrl} download={c.pdfUrl}>
            <button type="button" className="btn case-study-download">
              Download
            </button>
          </a>
        </div>
      ))}
    </div>
  );
}
